/**
 * Host-mode bridge. When sim3d runs INSIDE Cyberfrog (served from the learner
 * plane at /t/<token>), the host page injects:
 *
 *   window.__CF_SIM__ = { scenarioId, token, apiBase, language }
 *
 * In that mode the SPA: (a) runs the named scenario without showing the course
 * library, (b) routes its dynamic calls (TTS / realtime token) to the Cyberfrog
 * /sim/* endpoints, and (c) reports the quiz to /t/<token>/quiz and behavioural
 * outcomes to /sim/track so results land in training_assignments + journey_outcomes
 * exactly like every other e-learning. Every POST carries X-CSRF-Token (the
 * cf_csrf cookie) and the page nonce in the body; the learner page heartbeats
 * to /t/<token>/event every 30 s (startHostHeartbeat, Human Risk plan 1c.21).
 *
 * When the object is absent (standalone sim-VPS / dev) every path falls back to
 * the original behaviour, so this file is inert outside Cyberfrog.
 */

/** Host-supplied replacement for a course's own designed intro card. */
export interface HostIntro {
  title?: string;
  summary?: string;
}

export interface HostConfig {
  hosted: boolean;
  scenarioId?: string;
  specUrl?: string;
  token?: string;
  /** API origin for cross-call prefixing. "" == same-origin (the normal case). */
  apiBase: string;
  language?: string;
  /** Override for the pre-rendered voice manifest location. */
  voiceManifestUrl?: string;
  /** Suppress the course's bespoke intro and use the generic card. */
  skipDesignedIntro?: boolean;
  customIntro?: HostIntro;
}

interface RawBootstrap {
  scenarioId?: string;
  specUrl?: string;
  token?: string;
  apiBase?: string;
  language?: string;
  voiceManifestUrl?: string;
  skipDesignedIntro?: boolean;
  customIntro?: HostIntro;
}

let cached: HostConfig | null = null;

export function getHostConfig(): HostConfig {
  if (cached) return cached;
  let raw: RawBootstrap | null = null;
  try {
    raw = (window as unknown as { __CF_SIM__?: RawBootstrap }).__CF_SIM__ ?? null;
  } catch {
    raw = null;
  }
  if (raw && (raw.token || raw.scenarioId || raw.specUrl)) {
    cached = {
      hosted: true,
      scenarioId: raw.scenarioId,
      specUrl: raw.specUrl,
      token: raw.token,
      apiBase: (raw.apiBase ?? "").replace(/\/+$/, ""),
      language: raw.language,
      voiceManifestUrl: raw.voiceManifestUrl,
      skipDesignedIntro: raw.skipDesignedIntro,
      customIntro: raw.customIntro,
    };
  } else {
    cached = { hosted: false, apiBase: "" };
  }
  return cached;
}

/** Resolve a bundled public asset (audio/avatars) under the build base.
 *  Standalone vite base is "/" (=> /audio/x); the Cyberfrog hosted build uses
 *  CF_BASE=/cdn/sim/ (=> /cdn/sim/audio/x), so the same code works on both
 *  planes. Scenario .spz/.glb/.mp4 assets keep their explicit /cdn/sim/* URLs
 *  (both Caddies map /cdn/sim there). */
export function publicAsset(path: string): string {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL || "/";
  const p = path.replace(/^\//, "");
  return `${base}${p}`;
}

/** Prefix a path with the host apiBase (no-op for same-origin / standalone). */
export function apiUrl(path: string): string {
  const { apiBase } = getHostConfig();
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${apiBase}${p}`;
}

/** Hosted mode only: true on a hub exercise page (?m=<spoke>). The hub's own quiz is the
 *  graded one, so a spoke never reports a quiz or tracks module events for the assignment. */
export function isSpokeLoad(): boolean {
  if (!getHostConfig().hosted) return false;
  try {
    return new URLSearchParams(window.location.search).has("m");
  } catch {
    return false;
  }
}

/** Hub exercise link. Standalone: the href as authored ("/?scenario=<id>&..."). Hosted: stay on
 *  the training page (current path) and name the spoke with ?m=, the rest of the query unchanged. */
export function hubExerciseHref(href: string): string {
  if (!getHostConfig().hosted) return href;
  const m = /^\/\?scenario=([a-z0-9-]{1,64})(&.*)?$/.exec(href);
  if (m) return `${window.location.pathname}?m=${m[1]}${m[2] ?? ""}`;
  // Spec-only courses (bar-walkable) are linked as ?spec=/cdn/sim/<id>/<file>.json.
  const sp = /^\/\?spec=([^&]+)(&.*)?$/.exec(href);
  if (sp) {
    let url = "";
    try {
      url = decodeURIComponent(sp[1]);
    } catch {
      return href;
    }
    const id = /^\/cdn\/sim\/([a-z0-9-]{1,64})\//.exec(url);
    if (id) return `${window.location.pathname}?m=${id[1]}${sp[2] ?? ""}`;
  }
  return href;
}

/** "Back to the terminal". Standalone: the hub scenario URL. Hosted: the current training page
 *  without ?m= (the hub is the page's own scenario); beat=b_hub + resume=1 land on the live hub. */
export function hubBackHref(returnId: string): string {
  if (!getHostConfig().hosted) return `/?scenario=${returnId}&beat=b_hub&resume=1`;
  return `${window.location.pathname}?beat=b_hub&resume=1`;
}

/** The cf_csrf cookie the /t/<token> shell sets: the double-submit token of /quiz, /quiz-check and
 *  /sim/track, and the page nonce the server registered for this assignment. Absent in previews. */
export function readCsrfCookie(): string | null {
  try {
    const m = document.cookie.match(/(?:^|; )cf_csrf=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

let bootNonce: string | null | undefined;

/** This page's own nonce: the cookie as it was when the SPA booted. A second tab overwrites the cookie,
 *  but every page's nonce stays registered for the assignment, so /event accepts either. */
export function hostPageNonce(): string | null {
  if (bootNonce === undefined) bootNonce = readCsrfCookie();
  return bootNonce ?? readCsrfCookie();
}

/** Headers of every hosted POST: JSON plus X-CSRF-Token = the live cookie (what the double-submit compares). */
export function hostPostHeaders(): Record<string, string> {
  const csrf = readCsrfCookie();
  return csrf ? { "Content-Type": "application/json", "X-CSRF-Token": csrf } : { "Content-Type": "application/json" };
}

/** JSON body of a hosted POST with the page nonce added (/t/<token>/event reads it; the other endpoints ignore it). */
export function hostBody(body: Record<string, unknown>): string {
  const nonce = hostPageNonce();
  return JSON.stringify(nonce ? { ...body, nonce } : body);
}

const HEARTBEAT_MS = 30_000;
let heartbeatStarted = false;

/** Learner page only (/t/<token>): a heartbeat to /t/<token>/event every 30 s while the tab is visible, with the
 *  page nonce in the body; `gestured` once the learner pressed, tapped or typed. The server credits one 30 s window
 *  per assignment, shared with the shell's own heartbeat, so the two never double the time. Inert standalone,
 *  in admin previews and on share links (no learner page, no cf_csrf cookie). Never throws. */
export function startHostHeartbeat(): void {
  const host = getHostConfig();
  if (heartbeatStarted || !host.hosted || !host.token) return;
  try {
    if (!window.location.pathname.startsWith("/t/")) return;
  } catch {
    return;
  }
  if (!hostPageNonce()) return;
  heartbeatStarted = true;
  let gestured = false;
  const kinds = ["pointerdown", "keydown", "touchstart"] as const;
  const mark = () => {
    gestured = true;
    kinds.forEach((k) => window.removeEventListener(k, mark, true));
  };
  kinds.forEach((k) => window.addEventListener(k, mark, { capture: true, passive: true }));
  window.setInterval(() => {
    try {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      void fetch(apiUrl(`/t/${host.token}/event`), {
        method: "POST",
        headers: hostPostHeaders(),
        credentials: "include",
        keepalive: true,
        body: hostBody({ event_type: "heartbeat", payload: { source: "spa" }, gestured }),
      }).catch(() => undefined);
    } catch {
      /* best-effort */
    }
  }, HEARTBEAT_MS);
}

/** Best-effort hosted-course module analytics. Inert on the standalone site. */
export function trackModule(moduleId: string, event: "opened" | "completed" | "finished" = "opened"): void {
  const host = getHostConfig();
  if (!host.hosted || !host.token) return;
  if (isSpokeLoad()) return;
  void fetch(apiUrl("/sim/track"), {
    method: "POST",
    headers: hostPostHeaders(),
    credentials: "include",
    keepalive: true,
    body: hostBody({
      token: host.token,
      kind: "sim_module",
      module_id: moduleId,
      event,
    }),
  }).catch(() => undefined);
}

/** Records the learner's acknowledgement of policy versions (POST /sim/policy-ack/<token>). Learner page only:
 *  previews, share links and the standalone site have no token or no cf_csrf cookie. Best-effort: the course never
 *  waits for it, and the post never completes the course. */
export function ackPolicies(versionIds: string[]): void {
  const host = getHostConfig();
  if (!host.hosted || !host.token || !versionIds.length || !readCsrfCookie()) return;
  void fetch(apiUrl(`/sim/policy-ack/${encodeURIComponent(host.token)}`), {
    method: "POST",
    headers: hostPostHeaders(),
    credentials: "include",
    keepalive: true,
    body: hostBody({ versionIds }),
  }).catch(() => undefined);
}

/** The server's verdict on one quiz answer (POST /t/<token>/quiz-check). */
export interface HostCheck {
  correct: boolean;
  rationale?: string;
}

/** Server grading of one quiz answer, used when the spec came without answer keys (`hostGraded`). `position` is
 *  the raw index in the quiz beat, `choice` the option id. null when the check could not be made (network, 4xx,
 *  timeout after 8 s). The first check of a question in an attempt is what /quiz grades. */
export async function checkQuizAnswer(position: number, choice: string): Promise<HostCheck | null> {
  const host = getHostConfig();
  if (!host.hosted || !host.token || !readCsrfCookie()) return null;
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? window.setTimeout(() => ctl.abort(), 8000) : 0;
  try {
    const res = await fetch(apiUrl(`/t/${host.token}/quiz-check`), {
      method: "POST",
      headers: hostPostHeaders(),
      credentials: "include",
      body: hostBody({ question_position: position, choice }),
      signal: ctl?.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { correct?: unknown; rationale?: unknown };
    return {
      correct: data.correct === true,
      rationale: typeof data.rationale === "string" && data.rationale ? data.rationale : undefined,
    };
  } catch {
    return null;
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

/** Partner brand the host page may pass as window.__CF_SIM__.brand = {product, logo_url, mascot}.
 *  Untrusted display data: text is stripped of control/bidi characters and capped, the logo must be a
 *  same-origin path or an https URL, mascot must be literally true. Absent or unusable => null, and the
 *  chrome shows the neutral default (no vendor name). Read independently of hosted mode. */
export interface HostBrand {
  product: string;
  logoUrl?: string;
  mascot: boolean;
}

export const NEUTRAL_PRODUCT = "Security Awareness";
export const NEUTRAL_KICKER = "Interactive 3D Training";

let cachedBrand: HostBrand | null | undefined;

function cleanBrandText(v: unknown, max: number): string {
  if (typeof v !== "string") return "";
  return v
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max)
    .trim();
}

function cleanBrandLogo(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim();
  if (!s || s.length > 512) return undefined;
  if (/^\/(?!\/)[A-Za-z0-9._~\-\/%]+(\?[A-Za-z0-9._~\-=&%]*)?$/.test(s)) return s;
  try {
    const u = new URL(s);
    if (u.protocol === "https:" && !u.username && !u.password) return u.href;
  } catch {
    /* not a URL */
  }
  return undefined;
}

export function getHostBrand(): HostBrand | null {
  if (cachedBrand !== undefined) return cachedBrand;
  let raw: unknown = null;
  try {
    raw = (window as unknown as { __CF_SIM__?: { brand?: unknown } }).__CF_SIM__?.brand ?? null;
  } catch {
    raw = null;
  }
  cachedBrand = null;
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    const product = cleanBrandText(r.product, 60);
    const logoUrl = cleanBrandLogo(r.logo_url ?? r.logoUrl);
    if (product || logoUrl) {
      cachedBrand = { product: product || NEUTRAL_PRODUCT, logoUrl, mascot: r.mascot === true };
    }
  }
  return cachedBrand;
}

/** Product name for chrome text: the partner's, else the neutral default. */
export function brandProductName(): string {
  return getHostBrand()?.product ?? NEUTRAL_PRODUCT;
}

/** Page title follows the partner brand when one is passed; otherwise index.html / the host shell keeps its own. */
export function applyBrandDocumentTitle(): void {
  const b = getHostBrand();
  if (!b) return;
  try {
    document.title = `${b.product} \u00b7 ${NEUTRAL_KICKER}`;
  } catch {
    /* ignore */
  }
}
